import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/Alert';
import { articleService } from '../../../api/articleService';
import CreateArticleModal from '../../../components/modals/CreateArticleModal';
import ViewArticleModal from '../../../components/modals/ViewArticleModal';
import toast from 'react-hot-toast';
import authService from '../../../api/authService';
import {
  FileText, 
  Search, 
  Filter, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Calendar, 
  Tag, 
  User, 
  MessageSquare, 
  Star, 
  TrendingUp, 
  BookOpen, 
  Archive,
  BarChart3
} from 'lucide-react';

const SEARCH_DEBOUNCE_MS = 400;

/** Safe defaults when menu permissions are missing (e.g. during logout teardown). */
const EMPTY_SCREEN_PERMISSIONS = Object.freeze({
  canView: false,
  canUpdate: false,
  canInsert: false,
  canDelete: false,
});

function getScreenPermissionsFromSession() {
  try {
    const raw = sessionStorage.getItem('menu');
    if (!raw) {
      return EMPTY_SCREEN_PERMISSIONS;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return EMPTY_SCREEN_PERMISSIONS;
    }
    return parsed;
  } catch {
    return EMPTY_SCREEN_PERMISSIONS;
  }
}


const MyArticles = () => {
  const [articles, setArticles] = useState([]);
  const [summaryStats, setSummaryStats] = useState({
    totalArticles: 0,
    published: 0,
    drafts: 0,
    totalViews: 0
  });
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [listBusy, setListBusy] = useState(true);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [error, setError] = useState(null);
  /** Immediate input value (controlled). */
  const [searchInput, setSearchInput] = useState('');
  /** Value that triggers API search (debounced). Empty string = show full tenant list from GET. */
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const currentUser = authService.getCurrentUser();
  const rawRole = (currentUser?.roleCode || currentUser?.role || sessionStorage.getItem('roleCode') || '').toString();
  const roleNorm = rawRole.toLowerCase().replace(/-/g, '_');
  /** Staff roles that should see every article status for their tenant (GET all + search). */
  const KNOWLEDGE_MANAGER_ROLES = new Set([
    'admin', 'administrator', 'super_admin', 'superadmin', 'agent', 'team_lead', 'developer'
  ]);
  const canManageKnowledgeForTenant = KNOWLEDGE_MANAGER_ROLES.has(roleNorm);

  /** Only admins and team leads may approve/publish drafts; agents see drafts but cannot approve. */
  const KB_ARTICLE_APPROVER_ROLES = new Set([
    'admin',
    'administrator',
    'super_admin',
    'superadmin',
    'team_lead'
  ]);
  const canApproveKbArticle = KB_ARTICLE_APPROVER_ROLES.has(roleNorm);

  const resolveTenantIdForArticles = () => {
    if (currentUser?.tenantId != null && currentUser.tenantId !== '') {
      const n = Number(currentUser.tenantId);
      if (Number.isFinite(n)) return n;
    }
    const s = sessionStorage.getItem('tenantId');
    if (s) {
      const n = parseInt(s, 10);
      if (Number.isFinite(n)) return n;
    }
    return undefined;
  };

  const categories = categoryOptions.length > 0
    ? categoryOptions
    : [...new Set(articles.map(article => article.category).filter(Boolean))];

  // Helper function to format date relative to now
  const formatRelativeDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    if (diffInDays === 1) return '1 day ago';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    return date.toLocaleDateString();
  };

  // Transform backend article to frontend format
  const transformArticle = (article) => {
    const status = (article.status || 'draft').toLowerCase();
    // Map 'active' status to 'published' for display consistency
    const displayStatus = status === 'active' ? 'published' : status;
    
    return {
      id: `ART-${String(article.articleId).padStart(3, '0')}`,
      articleId: article.articleId,
      title: article.title || 'Untitled Article',
      status: displayStatus,
      originalStatus: status, // Keep original status for API calls
      category: article.category || 'Uncategorized',
      views: article.usedCount || 0,
      helpful: article.helpfulCount || 0,
      notHelpful: article.notHelpfulCount || 0,
      createdDate: article.createdAt ? new Date(article.createdAt).toISOString().split('T')[0] : null,
      lastModified: article.updatedAt ? new Date(article.updatedAt).toISOString().split('T')[0] : null,
      author: article.createdBy || 'Unknown',
      tags: article.tags || [],
      question: article.question || '',
      answer: article.answer || '',
      content: article.answer || article.question || 'No content available',
      lastUpdate: formatRelativeDate(article.updatedAt || article.createdAt),
      roleId: article.roleId || null // Preserve roleId for dropdown selection
    };
  };

  /** Status / category / sort only; search text is applied by the API when debouncedSearch is non-empty. */
  const filteredArticles = articles
    .filter((article) => {
      if (filterStatus === 'all') return true;
      // Handle status mapping: 'published' filter should match both 'published' and 'active'
      if (filterStatus === 'published') {
        return article.status === 'published' || article.originalStatus === 'active';
      }
      return article.status === filterStatus;
    })
    .filter(article => filterCategory === 'all' || article.category === filterCategory)
    .sort((a, b) => {
      if (sortBy === 'newest') {
        const dateA = a.lastModified ? new Date(a.lastModified) : new Date(0);
        const dateB = b.lastModified ? new Date(b.lastModified) : new Date(0);
        return dateB - dateA;
      }
      if (sortBy === 'oldest') {
        const dateA = a.lastModified ? new Date(a.lastModified) : new Date(0);
        const dateB = b.lastModified ? new Date(b.lastModified) : new Date(0);
        return dateA - dateB;
      }
      if (sortBy === 'most-viewed') {
        return (b.views || 0) - (a.views || 0);
      }
      if (sortBy === 'most-helpful') {
        return (b.helpful || 0) - (a.helpful || 0);
      }
      return 0;
    });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
      case 'active': return <Badge variant="success">Published</Badge>;
      case 'draft': return <Badge variant="warning" className="bg-yellow-500 text-white">Draft</Badge>;
      case 'pending-review': return <Badge variant="info">Pending Review</Badge>;
      case 'archived': return <Badge variant="secondary">Archived</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Technical Support': return 'text-blue-600';
      case 'Email Support': return 'text-green-600';
      case 'Software Support': return 'text-purple-600';
      case 'Network Support': return 'text-orange-600';
      default: return 'text-gray-600';
    }
  };

  const parseArticlesResponse = (response) => {
    let articlesData = [];
    if (response?.data?.data) {
      articlesData = Array.isArray(response.data.data) ? response.data.data : [];
    } else if (Array.isArray(response?.data)) {
      articlesData = response.data;
    } else if (response?.data && typeof response.data === 'object' && !response.data.status) {
      articlesData = Array.isArray(response.data) ? response.data : [];
    }
    return articlesData;
  };

  const loadFullList = useCallback(async () => {
    setListBusy(true);
    setError(null);
    try {
      const tenantId = resolveTenantIdForArticles();
      const params = { tenantId };
      if (canManageKnowledgeForTenant) {
        params.includeDrafts = true;
      }

      const response = await articleService.getArticles(params);
      let filteredData = parseArticlesResponse(response);

      if (!canManageKnowledgeForTenant) {
        filteredData = filteredData.filter((a) => {
          const status = (a.status || '').toLowerCase();
          return status !== 'draft';
        });
      }

      const transformedArticles = filteredData.map(transformArticle);
      const totalArticles = transformedArticles.length;
      const published = transformedArticles.filter((a) => {
        const s = (a.originalStatus || a.status || '').toLowerCase();
        return s === 'active' || s === 'published';
      }).length;
      const drafts = transformedArticles.filter((a) => (a.originalStatus || a.status || '').toLowerCase() === 'draft').length;
      const totalViews = transformedArticles.reduce((sum, article) => sum + (article.views || 0), 0);
      setSummaryStats({ totalArticles, published, drafts, totalViews });
      setCategoryOptions([...new Set(transformedArticles.map((a) => a.category).filter(Boolean))]);
      setArticles(transformedArticles);
    } catch (err) {
      console.error('Error loading articles:', err);
      setError(err.message || 'Failed to load articles');
      toast.error('Failed to load articles');
    } finally {
      setListBusy(false);
      setInitialFetchDone(true);
    }
  }, [canManageKnowledgeForTenant]);

  const runApiSearch = useCallback(async (query) => {
    const q = (query || '').trim();
    if (!q) return;

    setListBusy(true);
    setError(null);
    try {
      const tenantId = resolveTenantIdForArticles();
      const searchOpts = { tenantId };
      if (canManageKnowledgeForTenant && tenantId != null) {
        searchOpts.includeDrafts = true;
      }
      const response = await articleService.searchArticles(q, searchOpts);
      const articlesData = parseArticlesResponse(response);

      let filteredData = articlesData;
      if (!canManageKnowledgeForTenant) {
        filteredData = filteredData.filter((a) => {
          const status = (a.status || '').toLowerCase();
          return status !== 'draft';
        });
      }

      setArticles(filteredData.map(transformArticle));
    } catch (err) {
      console.error('Error searching articles:', err);
      setError(err.message || 'Failed to search articles');
      toast.error('Failed to search articles');
    } finally {
      setListBusy(false);
      setInitialFetchDone(true);
    }
  }, [canManageKnowledgeForTenant]);

const hasPermission = getScreenPermissionsFromSession();

  /** Debounce search input: empty clears immediately; typing waits before calling the search API. */
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === '') {
      setDebouncedSearch('');
      return;
    }
    const t = setTimeout(() => setDebouncedSearch(trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (debouncedSearch === '') {
      loadFullList();
    } else {
      runApiSearch(debouncedSearch);
    }
  }, [debouncedSearch, loadFullList, runApiSearch]);

  const reloadList = useCallback(async () => {
    if (debouncedSearch.trim() === '') {
      await loadFullList();
    } else {
      await runApiSearch(debouncedSearch);
    }
  }, [debouncedSearch, loadFullList, runApiSearch]);

  const handleDeleteArticle = async (articleId) => {
    // TODO: Implement delete API call when available
    setArticles(articles.filter(article => article.id !== articleId));
    toast.success('Article deleted successfully');
  };

  const handlePublishArticle = async (articleId) => {
    try {
      const article = articles.find(a => a.id === articleId);
      if (!article) return;

      // Update article status to published
      await articleService.updateArticle(article.articleId, {
        ...article,
        status: 'active'
      });

      setArticles(articles.map(a => 
        a.id === articleId 
          ? { ...a, status: 'active', lastModified: new Date().toISOString().split('T')[0] }
          : a
      ));
      
      toast.success('Article published successfully');
      await reloadList(); // Reload to refresh the list
    } catch (error) {
      console.error('Error publishing article:', error);
      toast.error('Failed to publish article');
    }
  };

  const handleApproveArticle = async (articleId) => {
    if (!canApproveKbArticle) {
      toast.error('Only an administrator or team lead can approve articles.');
      return;
    }
    try {
      const article = articles.find(a => a.id === articleId || a.articleId === articleId);
      if (!article) {
        toast.error('Article not found');
        return;
      }

      // Call approve API endpoint
      await articleService.approveArticle(article.articleId);

      // Update local state
      setArticles(articles.map(a => 
        a.id === articleId || a.articleId === article.articleId
          ? { ...a, status: 'active', lastModified: new Date().toISOString().split('T')[0] }
          : a
      ));
      
      toast.success('Article approved and published successfully');
      await reloadList(); // Reload to refresh the list
    } catch (error) {
      console.error('Error approving article:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to approve article';
      toast.error(errorMessage);
    }
  };

  const handleCreateArticle = () => {
    setSelectedArticle(null);
    setShowCreateModal(true);
  };

  const handleViewArticle = async (articleId) => {
    try {
      const article = articles.find(a => a.id === articleId || a.articleId === articleId);
      if (article) {
        // If we need to fetch full details, do it here
        // For now, use the article from the list
        setSelectedArticle(article);
        setShowViewModal(true);
      } else {
        // Fetch article details from API if not in list
        const response = await articleService.getArticleById(articleId, { includeDrafts: true });
        const responseData = response?.data;
        const articleData = responseData?.data || responseData;
        if (articleData) {
          setSelectedArticle(transformArticle(articleData));
          setShowViewModal(true);
        } else {
          toast.error('Article not found');
        }
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      toast.error('Failed to load article details');
    }
  };

  const handleEditArticle = async (articleId) => {
    try {
      const article = articles.find(a => a.id === articleId || a.articleId === articleId);
      if (article) {
        // Fetch full article details for editing
        const response = await articleService.getArticleById(article.articleId, { includeDrafts: true });
        const responseData = response?.data;
        const articleData = responseData?.data || responseData;
        if (articleData) {
          // Transform article - transformArticle already includes question and answer
          const fullArticle = transformArticle(articleData);
          setSelectedArticle(fullArticle);
          setShowEditModal(true);
        } else {
          // Fallback to article from list
          setSelectedArticle(article);
          setShowEditModal(true);
        }
      } else {
        toast.error('Article not found');
      }
    } catch (error) {
      console.error('Error fetching article for editing:', error);
      toast.error('Failed to load article for editing');
    }
  };

  const handleSaveArticle = async (articleData, articleId = null) => {
    try {
      let response;
      if (articleId) {
        // Update existing article
        //console.log('Updating article with ID:', articleId, 'data:', articleData);
        response = await articleService.updateArticle(articleId, articleData);
      } else {
        // Create new article
        //console.log('Creating article with data:', articleData);
        response = await articleService.createArticle(articleData);
      }
      //console.log('Article save response:', response);
      
      // Registration service ResponseBean structure: { status, message, data }
      // apiRequest returns the JSON directly, articleService wraps it in { data: ... }
      // So response structure is: { data: { status, message, data: { article } } }
      const responseBean = response?.data;
      const status = responseBean?.status;
      const savedArticle = responseBean?.data;
      const message = responseBean?.message;
      
      //console.log('Parsed response - status:', status, 'message:', message, 'article:', savedArticle);
      
      // Check for success:
      // 1. Status contains 201/200 or CREATED/OK (HttpStatus serializes as "201 CREATED" or just 201)
      // 2. Article data exists with articleId
      // 3. Message contains "success"
      const statusStr = status ? status.toString() : '';
      const isSuccess = 
        statusStr.includes('201') || 
        statusStr.includes('200') ||
        statusStr.includes('CREATED') ||
        statusStr.includes('OK') ||
        status === 201 ||
        status === 200 ||
        (savedArticle && savedArticle.articleId) ||
        (message && message.toLowerCase().includes('success'));
      
      if (isSuccess) {
        toast.success(message || (articleId ? 'Article updated successfully!' : 'Article created successfully!'));
        setShowCreateModal(false);
        setShowEditModal(false);
        setSelectedArticle(null);
        
        // Reload articles to show the updated/new one
        await reloadList();
        
        // Return saved article for file upload
        return savedArticle;
      } else {
        throw new Error(message || `Failed to ${articleId ? 'update' : 'create'} article - unexpected response format`);
      }
    } catch (error) {
      console.error(`Error ${articleId ? 'updating' : 'creating'} article - full error:`, error);
      console.error('Error stack:', error.stack);
      console.error('Error response:', error.response);
      
      // Extract error message from various possible structures
      let errorMessage = `Failed to ${articleId ? 'update' : 'create'} article`;
      
      if (error.response?.data) {
        // Axios-style error response
        const errorData = error.response.data;
        errorMessage = errorData.message || errorData.error || errorData.data?.message || errorMessage;
      } else if (error.message) {
        // Direct error message
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      // Only show error toast if it's a real error (not a success message)
      if (!errorMessage.toLowerCase().includes('success')) {
        toast.error(errorMessage);
      }
      throw error; // Re-throw to let modal handle it
    }
  };

  if (!initialFetchDone && listBusy) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading your articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header with Create Article Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">My Articles</h1>
        {hasPermission?.canInsert && (
        <Button 
          onClick={handleCreateArticle}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="h-4 w-4" />
          Create Article
        </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Articles</CardTitle>
            <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">{summaryStats.totalArticles}</div>
            <p className="text-xs text-blue-600 dark:text-blue-400">Total articles</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-green-700 dark:text-green-300">Published</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-800 dark:text-green-200">{summaryStats.published}</div>
            <p className="text-xs text-green-600 dark:text-green-400">Live articles</p>
          </CardContent>
        </Card>
        {/* Drafts card - commented out as per user request */}
        {/* <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-yellow-700 dark:text-yellow-300">Drafts</CardTitle>
            <Edit className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-800 dark:text-yellow-200">{summaryStats.drafts}</div>
            <p className="text-xs text-yellow-600 dark:text-yellow-400">Work in progress</p>
          </CardContent>
        </Card> */}
        {/* Total Views card - commented out as per user request */}
        {/* <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-purple-700 dark:text-purple-300">Total Views</CardTitle>
            <Eye className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-800 dark:text-purple-200">{summaryStats.totalViews}</div>
            <p className="text-xs text-purple-600 dark:text-purple-400">All time views</p>
          </CardContent>
        </Card> */}
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row gap-4 items-center">
        <div className="relative w-full md:w-1/3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400" />
          <Input
            type="text"
            placeholder="Search articles..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9 pr-3 py-2 border rounded-md w-full"
          />
        </div>

        <div className="flex gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="p-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Statuses</option>
            <option value="published">Published</option>
            {canManageKnowledgeForTenant && <option value="draft">Draft</option>}
            <option value="pending-review">Pending Review</option>
            <option value="archived">Archived</option>
          </select>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="p-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="p-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="most-viewed">Most Viewed</option>
            <option value="most-helpful">Most Helpful</option>
          </select>
        </div>
      </div>

      {/* Article List — listBusy after first paint avoids full-page flash while API search/list runs */}
      <div className={`relative space-y-4 transition-opacity ${listBusy && initialFetchDone ? 'opacity-80' : 'opacity-100'}`}>
        {listBusy && initialFetchDone && (
          <div className="absolute right-0 top-0 z-10 rounded-md bg-white/90 dark:bg-gray-900/90 px-2 py-1 text-xs text-gray-600 dark:text-gray-300 shadow border border-gray-200 dark:border-gray-700">
            Updating…
          </div>
        )}
        {filteredArticles.length === 0 ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No Articles Found</AlertTitle>
            <AlertDescription>
              No articles match your current search and filter criteria. Try adjusting your filters or create a new article.
            </AlertDescription>
          </Alert>
        ) : (
          filteredArticles.map(article => {
            const isDraft = (article.status || '').toLowerCase() === 'draft';
            const cardBgColor = isDraft ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' : '';
            return (
            <Card key={article.id} className={`hover:shadow-md transition-shadow ${cardBgColor}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{article.title}</h3>
                      {getStatusBadge(article.status)}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">ID:</span> {article.id} | 
                      <span className={`font-medium ml-1 ${getCategoryColor(article.category)}`}> {article.category}</span>
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                      {article.content}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <Eye className="h-4 w-4" /> {article.views} views
                      </div>
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4" /> {article.helpful} helpful
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" /> Modified: {article.lastModified}
                      </div>
                    </div>
                    {article.tags && article.tags.length > 0 && (
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <Tag className="h-4 w-4" /> 
                        {article.tags.map(tag => (
                          <Badge key={tag} variant="secondary" className="mr-1">{tag}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="mt-4 md:mt-0 flex flex-col items-end space-y-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Last Update: {article.lastUpdate}</span>
                    <div className="flex gap-2">
                    {hasPermission?.canView && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleViewArticle(article.id)}
                      >
                        <Eye className="h-4 w-4 mr-1" /> View
                      </Button>
                      )}
                      {hasPermission?.canUpdate && (
                      <Button 
                        variant="secondary" 
                        size="sm"
                        onClick={() => handleEditArticle(article.id)}
                      >
                        <Edit className="h-4 w-4 mr-1" /> Edit
                      </Button>
                      )}
                      {isDraft && canApproveKbArticle && (
                        <Button 
                          variant="primary" 
                          size="sm"
                          onClick={() => handleApproveArticle(article.id)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" /> Approve
                        </Button>
                      )}
                      {isDraft && !canApproveKbArticle && (
                        <Button 
                          variant="secondary" 
                          size="sm"
                          disabled
                          className="opacity-50 cursor-not-allowed"
                          title="Ask an administrator or team lead to approve this draft"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" /> Pending approval
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteArticle(article.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })
        )}
      </div>

      {/* Create Article Modal */}
      <CreateArticleModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedArticle(null);
        }}
        onSave={handleSaveArticle}
        categories={categories}
      />

      {/* Edit Article Modal */}
      <CreateArticleModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedArticle(null);
        }}
        onSave={(articleData) => handleSaveArticle(articleData, selectedArticle?.articleId)}
        categories={categories}
        article={selectedArticle}
      />

      {/* View Article Modal */}
      <ViewArticleModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedArticle(null);
        }}
        article={selectedArticle}
      />
    </div>
  );
};

export default MyArticles;
